import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingAccent, marketingButton, marketingType } from "../../marketing.type";

export type Props = {
  eyebrow?: string;
  heading: string;
  /** One paragraph per line, as the design breaks them */
  description?: string | string[];
  ctaLabel: string;
  /** Where the call to action goes */
  href?: string;
  onClick?: () => void;
  /** The colour of the band this sits in; the card takes the opposite */
  tone?: "light" | "dark";
  /**
   * Keep the band's own colour and draw a rule round the card instead, as
   * the design draws Donate on the dark Vision band.
   */
  outlined?: boolean;
  /** Centred for the asks, left-set for Donate, as the design draws each */
  align?: "center" | "start";
  /**
   * Set the paragraphs left in a narrower column while everything else stays
   * centred, which is how the design sets a card with a long argument in it.
   */
  bodyAlign?: "inherit" | "start";
  className?: string;
};

/**
 * The card that asks for something, in the opposite colour to its surroundings.
 *
 * It appears at the end of each part of the page, repeating the ask for the
 * audience that part was addressed to. The inversion is the point: it is the
 * one thing on the band that is not the same colour as everything around it.
 */
export function MarketingCtaCard({
  eyebrow,
  heading,
  description,
  ctaLabel,
  href,
  onClick,
  tone = "light",
  outlined = false,
  align = "center",
  bodyAlign = "inherit",
  className,
}: Props) {
  // Which colour the card itself is: the opposite of the band, unless it is
  // outlined and keeps the band's.
  const inverted = outlined ? tone === "dark" : tone === "light";
  const paragraphs =
    description == null
      ? []
      : Array.isArray(description)
        ? description
        : [description];

  return (
    <div
      data-testid="marketing-cta-card"
      // The two are drawn differently at every width: the centred card with
      // a 26px corner and 24px of padding, the left-set one with 36 and 32.
      className={`flex flex-col ${
        align === "center"
          ? "items-center text-center rounded-[26px] p-6 gap-6 lg:gap-8"
          : "items-start rounded-[36px] p-8 gap-5"
      } ${
        inverted ? "bg-brand-800 text-brand-50" : "bg-brand-50 text-brand-800"
      } ${outlined ? (inverted ? "border border-brand-50" : "border border-brand-800") : ""} ${className ?? ""}`}
    >
      {eyebrow && (
        <Type
          variant="caption"
          as="p"
          className={`${marketingType.cardEyebrow} ${marketingAccent} max-w-none`}
        >
          {eyebrow}
        </Type>
      )}
      <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayWeight} ${marketingType.displayTrim}`}>
        {heading}
      </Type>
      {paragraphs.length > 0 && (
        <div
          className={`flex flex-col gap-4 ${
            bodyAlign === "start" ? "w-full max-w-[800px] text-left" : ""
          }`}
        >
          {paragraphs.map((paragraph) => (
            <Type
              variant="body2"
              key={paragraph}
              className={`max-w-none whitespace-pre-line ${
                align === "start" ? marketingType.body : marketingType.small
              }`}
            >
              {paragraph}
            </Type>
          ))}
        </div>
      )}
      <Button
        as={href ? "a" : undefined}
        href={href}
        onClick={onClick}
        theme={inverted ? "dark" : "light"}
        variant="primary"
        // The left-set card's button fills the card until a desktop, where
        // it is the design's small 48px pill. The centred card's is 41px
        // tall at every width.
        // The left-set card's pill is as wide as its word: 18px either side
        // and 2px of the label's own, so the shared button's label padding
        // is taken back. Its rule is the dark green, as the design draws it.
        className={`px-4 ${
          align === "start"
            ? "mt-1 w-full h-9 border-brand-800 lg:w-auto lg:h-12 lg:px-[18px]"
            : "mt-2 lg:mt-0 w-full h-[41px] border-brand-700 md:w-[216px] lg:w-auto lg:min-w-[224px]"
        }`}
        typeClassName={
          align === "start"
            ? `${marketingButton.compactTypeClassName} lg:px-0.5`
            : marketingButton.typeClassName
        }
        data-testid="marketing-cta-card-button"
      >
        {ctaLabel}
      </Button>
    </div>
  );
}

export default MarketingCtaCard;